import React, { useState } from 'react';

import { DialogActions, Typography } from '@mui/material';
import { useMountedState } from 'react-use';
import {
  AlineButton,
  AnimatedAlineDialog,
  ConfirmationDialog,
  ConfirmationDialogVariant,
} from 'src/components';
import { getTypographyForSx } from 'src/theme';
import type { ReactSetStateAction } from 'src/types';
import { makeStyles } from 'tss-react/mui';

import type { MentionEntityOwner } from './hooks';
import type { MentionEntity } from './types';

const useStyles = makeStyles()(theme => ({
  dialogBody: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
  },
  listItem: {
    ...getTypographyForSx(theme.typography.subtitle1),
    marginBottom: theme.spacing(1),
  },
  paper: {
    width: theme.dialogStyles.standardDialogWidth,
  },
}));

interface GrantMentionedUsersAccessDialogProps {
  emailsWithoutAccess: string[];
  mentionEntity: MentionEntity;
  mentionEntityOwner: MentionEntityOwner | null;
  onSubmit: () => void;
  open: boolean;
  setOpen: ReactSetStateAction<boolean>;
  variant: 'grant-access' | 'request-access';
  requestAccessMsg?: string;
}

function getRequestAccessMsg(mentionEntityOwner: MentionEntityOwner | null): string {
  const defaultMsg =
    'Some mentioned users lack access, but you are not authorized to grant access. You can request access from the owner';
  if (!mentionEntityOwner) return `${defaultMsg}.`;
  if (mentionEntityOwner.displayName.toLowerCase() === mentionEntityOwner.email.toLowerCase())
    return `${defaultMsg} ${mentionEntityOwner.email}.`;

  return `${defaultMsg} ${mentionEntityOwner.displayName} (${mentionEntityOwner.email}).`;
}

export function GrantMentionedUsersAccessDialog({
  emailsWithoutAccess,
  mentionEntity,
  mentionEntityOwner,
  onSubmit,
  open,
  requestAccessMsg = getRequestAccessMsg(mentionEntityOwner),
  setOpen,
  variant,
}: GrantMentionedUsersAccessDialogProps): JSX.Element | null {
  const { classes } = useStyles();
  const isMounted = useMountedState();
  const [submitting, setSubmitting] = useState<boolean>(false);

  if (variant === 'request-access')
    return (
      <ConfirmationDialog
        message={requestAccessMsg}
        open={open}
        primaryText="Comment"
        title="Mentioned users lack access"
        variant={ConfirmationDialogVariant.Warn}
        onClickPrimary={onSubmit}
        onClose={() => setOpen(false)}
      />
    );

  return (
    <AnimatedAlineDialog
      classes={{ paper: classes.paper }}
      open={open}
      title="Mentioned users lack access"
      onClose={() => setOpen(false)}
    >
      <div className={classes.dialogBody}>
        <Typography>{`The following mentioned users do not have access to the ${mentionEntity.__typename.toLowerCase()} and will be granted access once your comment is submitted.`}</Typography>
        <ol>
          {[...emailsWithoutAccess]
            .sort((a, b) => a.localeCompare(b))
            .map((email, index) => {
              return (
                <li key={index} className={classes.listItem}>
                  <Typography variant="subtitle1">{email}</Typography>
                </li>
              );
            })}
        </ol>
      </div>
      <DialogActions>
        <AlineButton color="primary" variant="outlined" onClick={() => setOpen(false)}>
          Cancel
        </AlineButton>
        <AlineButton
          color="primary"
          loading={submitting}
          variant="contained"
          onClick={async () => {
            setSubmitting(true);
            await onSubmit();
            // Prevents setting state after dialog unmounts in some cases.
            if (!isMounted()) return;

            setSubmitting(false);
            setOpen(false);
          }}
        >
          Comment
        </AlineButton>
      </DialogActions>
    </AnimatedAlineDialog>
  );
}
